import axios from 'axios';

// Interface definitions
export interface Form {
    id: string;
    name: string;
    nameSlug?: string;
    description?: string;
    version?: string;
    processKey?: string;
    publicCode?: string;
    createdDate?: string;
    modifiedDate?: string;
    orgId?: string;
}

export interface FormContent {
    id: string;
    nameSlug: string;
    version: string;
    formioJson: string; // Base64 encoded JSON
    formioJsonContentType: string;
    form: Form;
}

export interface FormListResponse {
    content: Form[];
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
}

// Get auth token from localStorage
const getAuthToken = (): string => {
    try {
        const tokenData = localStorage.getItem('oidc.user:https://portal-eroyal.pmdgtech.com/auth/realms/doit:web_app');
        if (tokenData) {
            const parsedData = JSON.parse(tokenData);
            return parsedData.access_token || '';
        }
        return '';
    } catch (error) {
        console.error('Error retrieving access token:', error);
        return '';
    }
};

// Create API instance
const designerApi = axios.create({
    baseURL: import.meta.env.MODE === 'development' ? '/designer/api' : '/designer/api',
});

// Add request interceptor for auth token
designerApi.interceptors.request.use(
    (config) => {
        const token = getAuthToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Get paginated list of forms
export const getForms = async (
    page: number = 0,
    size: number = 10,
    search?: string,
    sort?: string
): Promise<FormListResponse> => {
    const params: Record<string, string | number> = { page, size };
    if (search) {
        params.search = search;
    }
    if (sort) {
        params.sort = sort;
    }
    const response = await designerApi.get<FormListResponse>('/forms', { params });
    return response.data;
};

// Get a single form by ID
export const getFormById = async (id: string): Promise<Form> => {
    const response = await designerApi.get<Form>(`/forms/${id}`);
    return response.data;
};

// Get form content by nameSlug and version
export const getFormContent = async (nameSlug: string, version: string): Promise<FormContent> => {
    try {
        const response = await designerApi.get<FormContent>(`/form-contents/${nameSlug}/${version}`);
        return response.data;
    } catch (error: any) {
        if (error.response?.status === 500 && error.response?.data?.detail?.includes('formContent" is null')) {
            // Return an empty mock FormContent because it doesn't exist yet
            return {
                id: '',
                nameSlug: nameSlug,
                version: version,
                formioJson: btoa(JSON.stringify({ display: "form", components: [] })),
                formioJsonContentType: "application/json",
                form: {} as any
            };
        }
        throw error;
    }
};

// Get form content by ID and version
export const getFormContentById = async (id: string, version: string): Promise<FormContent> => {
    const response = await designerApi.get<FormContent>(`/form-contents/${id}/${version}`);
    return response.data;
};

// Create a new form
export const createForm = async (data: Partial<Form>): Promise<Form> => {
    const response = await designerApi.post<Form>('/forms', data);
    return response.data;
};

// Update an existing form
export const updateForm = async (id: string, data: Partial<Form>): Promise<Form> => {
    const response = await designerApi.put<Form>(`/forms/${id}`, data);
    return response.data;
};

// Delete a form
export const deleteForm = async (id: string): Promise<void> => {
    await designerApi.delete(`/forms/${id}`);
};

// Save form content (formio JSON)
export const saveFormContent = async (data: {
    nameSlug: string;
    version: string;
    formioJson: string; // Base64 encoded
    formioJsonContentType: string;
    form: Form;
}): Promise<FormContent> => {
    const response = await designerApi.post<FormContent>('/form-contents', data);
    return response.data;
};

// Get all versions of a form by nameSlug
export const getFormVersions = async (nameSlug: string): Promise<FormContent[]> => {
    try {
        const response = await designerApi.get<FormContent[]>(`/form-contents/byslug/${nameSlug}/versions`);
        return Array.isArray(response.data) ? response.data : [];
    } catch (error: any) {
        if (error.response?.status === 500 && error.response?.data?.detail?.includes('formContent" is null')) {
            return [];
        }
        throw error;
    }
};

// Export all functions as default object
export default {
    getForms,
    getFormById,
    getFormContent,
    createForm,
    updateForm,
    deleteForm,
    saveFormContent
};
